import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Sensors as DeploymentIcon,
  GraphicEq as AudioIcon,
  CheckCircleOutline as ConfidenceIcon,
  Insights as DetectionIcon,
} from '@mui/icons-material';
import { MinimalBirdIcon as SpeciesIcon } from './MinimalBirdIcon';
import { BioDeployment, BioSite } from '../bioacousticMockData';
import SpeciesActivityTrendChart from './SpeciesActivityTrendChart';
import AcousticIndicesChart from './AcousticIndicesChart';
import SpeciesDistributionDonut from './SpeciesDistributionDonut';

interface DeploymentLevelSectionProps {
  site: BioSite;
  activeDeployment: BioDeployment;
  onSelectDeployment: (deployment: BioDeployment) => void;
  onAvgActivityTrend?: (species: any) => void;
}

const DeploymentLevelSection: React.FC<DeploymentLevelSectionProps> = ({
  site,
  activeDeployment,
  onSelectDeployment,
}) => {
  return (
    <Box
      id="deployment-level-section"
      sx={{
        mb: 8,
        pt: 3,
        borderTop: '1px dashed #2A2A2A',
      }}
    >
      {/* Level Header Banner */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                bgcolor: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '4px',
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <DeploymentIcon sx={{ fontSize: 13, color: '#8B5CF6' }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#8B5CF6', letterSpacing: '1px' }}>
                LEVEL 3 : DEPLOYMENT LEVEL
              </Typography>
            </Box>
            <Chip
              label="OPERATIONAL // LIVE"
              size="small"
              sx={{
                height: 18,
                fontSize: '9px',
                fontWeight: 700,
                color: '#10B981',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            {activeDeployment.deployment_name}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#666666', mt: 0.25 }}>
            {activeDeployment.device_type} ({activeDeployment.device_id}) • {activeDeployment.location_name} • {activeDeployment.startDate} to {activeDeployment.endDate}
          </Typography>
        </Box>

        {/* Switch Deployment Dropdown */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '11px', color: '#666666', fontWeight: 700, textTransform: 'uppercase' }}>
            Switch Deployment:
          </Typography>
          <Select
            size="small"
            value={activeDeployment.deployment_id}
            onChange={(e) => {
              const target = site.deployments.find((d) => d.deployment_id === e.target.value);
              if (target) onSelectDeployment(target);
            }}
            sx={{
              bgcolor: '#111111',
              color: '#8B5CF6',
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: '6px',
              border: '1px solid #2A2A2A',
              '& .MuiSelect-select': { py: 0.75, px: 1.5 },
              '& .MuiSvgIcon-root': { color: '#8B5CF6' },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: '#111',
                  border: '1px solid #222',
                  color: '#fff',
                  '& .MuiMenuItem-root': {
                    fontSize: '12px',
                    '&.Mui-selected': { bgcolor: 'rgba(139,92,246,0.15)', color: '#8B5CF6' },
                  },
                },
              },
            }}
          >
            {site.deployments.map((d) => (
              <MenuItem key={d.deployment_id} value={d.deployment_id}>
                {d.deployment_name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* Row 1: 4 KPIs in a Straight Line */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {[
          {
            label: 'Species Identified',
            value: activeDeployment.totalSpecies,
            sub: 'At this sensor node',
            icon: SpeciesIcon,
            color: '#10B981',
          },
          {
            label: 'Total Audio Files',
            value: activeDeployment.totalAudioFiles.toLocaleString(),
            sub: 'Processed & catalogued',
            icon: AudioIcon,
            color: '#00D4FF',
          },
          {
            label: 'Total Detections',
            value: activeDeployment.totalDetections,
            sub: 'Acoustic calls detected',
            icon: DetectionIcon,
            color: '#F59E0B',
          },
          {
            label: 'Avg Model Confidence',
            value: `${activeDeployment.avgConfidence}%`,
            sub: 'Neural classifier score',
            icon: ConfidenceIcon,
            color: '#8B5CF6',
          },
        ].map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Box
              key={kpi.label}
              sx={{
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid #1E1E1E',
                borderRadius: '12px',
                p: '16px 18px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  borderColor: `${kpi.color}55`,
                  background: 'rgba(255, 255, 255, 0.04)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography sx={{ fontSize: '10px', color: '#777777', fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  {kpi.label}
                </Typography>
                <IconComp sx={{ fontSize: 20, color: kpi.color, opacity: 0.85 }} />
              </Box>
              <Typography sx={{ fontSize: '26px', fontWeight: 800, color: '#FFFFFF', lineHeight: 1.1, my: 1 }}>
                {kpi.value}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: '#555555' }}>
                {kpi.sub}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Row 2: Acoustic Indices Analysis (Left) & Species Threat Distribution Donut (Right) beside each other */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.8fr' }, gap: 2, mb: 3 }}>
        <AcousticIndicesChart
          deploymentName={activeDeployment.deployment_name}
          height={340}
        />
        <SpeciesDistributionDonut
          data={activeDeployment.threatDistribution}
          title={`Species Threat Distribution (${activeDeployment.deployment_name})`}
          height={340}
        />
      </Box>

      {/* Row 3: Species Activity Full Width below them */}
      <Box sx={{ mb: 3 }}>
        <SpeciesActivityTrendChart
          title={`Species Activity (${activeDeployment.deployment_name})`}
          height={360}
        />
      </Box>
    </Box>
  );
};

export default DeploymentLevelSection;
